import { DebouncedSearchBar } from '@/components/features/Search/DebouncedSearchBar';
import { Sticky } from '@/config/sticky-layout';
import { Box, Container, HStack } from '@chakra-ui/react';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { PropsWithChildren } from 'react';
import SortingOptions from './Facets/SortingOptions';
import RefreshButton from './RefreshButton';
import { SortOption, SortOptionType } from './config';

interface SearchActionBarProps<T extends SortOptionType> extends PropsWithChildren {
  sortOptions: SortOption<T>[];
  sortOption: SortOption<T>;
  setSortOption: (option: SortOption<T>) => void;
  initialSearchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  isSortDisabled?: boolean;
  refetch: () => void;
  isLoading: boolean;
  hasTextSearch?: boolean;
}

const SearchActionBar = <T extends SortOptionType>({
  sortOptions,
  sortOption,
  setSortOption,
  initialSearchQuery,
  onSearchQueryChange,
  isSortDisabled,
  refetch,
  isLoading,
  hasTextSearch,
  children,
}: SearchActionBarProps<T>) => {
  const { t: tCommon } = useTranslation('common');

  return (
    <Sticky
      element="action-bar"
      bg="blackBg"
      mb="space.24"
      zIndex="sticky-layout"
      transition="box-shadow .1s ease-in"
      stuck={{
        animate: {
          boxShadow: 'sticky.action-bar',
        },
      }}
    >
      <Container variant="breakout">
        <HStack
          display={{ base: 'none', md: 'flex' }}
          alignItems="center"
          justifyContent="space-between"
          py="space.14"
          spacing="space.24"
        >
          <HStack spacing="space.16" w="100%">
            <RefreshButton
              isLoading={isLoading}
              onClick={() => refetch()}
              tooltipLabel={time => tCommon('last-updated', { time })}
            />

            {hasTextSearch && (
              <Box maxWidth={rem(445)} w="100%">
                <DebouncedSearchBar
                  initialSearchQuery={initialSearchQuery}
                  onSearchQueryChange={onSearchQueryChange}
                />
              </Box>
            )}
          </HStack>

          <HStack spacing="space.24" ml="auto">
            <SortingOptions<T>
              sortOptions={sortOptions}
              isDisabled={isSortDisabled}
              value={sortOption}
              onChange={setSortOption}
              variant="select"
            />
            {children}
          </HStack>
        </HStack>
      </Container>
    </Sticky>
  );
};

export default SearchActionBar;
